import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Tag, Calendar, CalendarDays, Users, SlidersHorizontal, ChevronDown, X, Building2, Euro } from 'lucide-react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../ui/sheet';
import { ACTIVITY_TYPE_LABELS } from '../../lib/constants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const activityTypeLabels = ACTIVITY_TYPE_LABELS;

// Dag-van-de-week opties (value = canonieke 2-letter code, zoals de backend verwacht).
const WEEKDAY_FILTER_OPTIONS = [
  { value: 'ma', key: 'weekday.monday' },
  { value: 'di', key: 'weekday.tuesday' },
  { value: 'wo', key: 'weekday.wednesday' },
  { value: 'do', key: 'weekday.thursday' },
  { value: 'vr', key: 'weekday.friday' },
  { value: 'za', key: 'weekday.saturday' },
  { value: 'zo', key: 'weekday.sunday' },
];

const buildCountParams = (pendingFilters, pendingSourceFilter) => {
  const p = {};
  if (pendingFilters.wijk) p.wijk = pendingFilters.wijk;
  if (pendingFilters.activity_type) p.activity_type = pendingFilters.activity_type;
  if (pendingFilters.date_filter) p.date_filter = pendingFilters.date_filter;
  if (pendingFilters.day) p.day = pendingFilters.day;
  if (pendingFilters.cost_filter) p.cost_filter = pendingFilters.cost_filter;
  if (pendingFilters.age_group && pendingFilters.age_group !== 'all') p.age_group = pendingFilters.age_group;
  if (pendingFilters.target_audience && pendingFilters.target_audience !== 'all') p.target_audience = pendingFilters.target_audience;
  if (pendingSourceFilter && pendingSourceFilter !== 'all') p.source = pendingSourceFilter;
  return p;
};

// Hook: fetch live activity-count voor de pending filters (debounced).
const useFilterCount = (pendingFilters, pendingSourceFilter, enabled) => {
  const [count, setCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!enabled) return undefined;
    if (timer.current) clearTimeout(timer.current);
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/activities/count`, { params: buildCountParams(pendingFilters, pendingSourceFilter) });
        setCount(res.data?.count ?? null);
      } catch {
        setCount(null);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [enabled, pendingFilters, pendingSourceFilter]);

  return { count, loading };
};

/**
 * Haalt per filter-dimensie (wijk, activity_type, target_audience) live het
 * aantal resultaten op zodat de labels "Sport (12)" of "Senioren (0)" kunnen
 * tonen. Debounced en cross-dimensional zoals het backend-endpoint.
 */
const useDimensionCounts = (pendingFilters) => {
  const [counts, setCounts] = useState(null);
  const timer = useRef(null);
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const params = {};
        if (pendingFilters?.wijk && pendingFilters.wijk !== 'all') params.wijk = pendingFilters.wijk;
        if (pendingFilters?.activity_type && pendingFilters.activity_type !== 'all') params.activity_type = pendingFilters.activity_type;
        if (pendingFilters?.target_audience && pendingFilters.target_audience !== 'all') params.target_audience = pendingFilters.target_audience;
        if (pendingFilters?.date_filter && pendingFilters.date_filter !== 'all') params.date_filter = pendingFilters.date_filter;
        if (pendingFilters?.cost_filter && pendingFilters.cost_filter !== 'all') params.cost_filter = pendingFilters.cost_filter;
        const res = await axios.get(`${API}/activities/filter-counts`, { params });
        setCounts(res.data || null);
      } catch {
        setCounts(null);
      }
    }, 400);
    return () => { if (timer.current) clearTimeout(timer.current); };
  }, [pendingFilters?.wijk, pendingFilters?.activity_type, pendingFilters?.target_audience, pendingFilters?.date_filter, pendingFilters?.cost_filter]);
  return counts;
};

// Toont "(N)" naast een filter-label; grijs als 0 om afhaakpunten zichtbaar te maken.
const CountBadge = ({ n }) => {
  if (n === null || n === undefined) return null;
  return (
    <span className={`ml-1.5 text-[10px] font-medium ${n === 0 ? 'text-slate-300' : 'text-slate-500'}`} data-testid={`filter-count-${n}`}>
      ({n})
    </span>
  );
};

const FilterSelects = ({ pendingFilters, wijken, activityTypes, onPendingFilterChange, t, pendingSourceFilter, onPendingSourceFilterChange }) => {
  const dimensionCounts = useDimensionCounts(pendingFilters);
  const wc = dimensionCounts?.wijk || {};
  const ac = dimensionCounts?.activity_type || {};
  const tc = dimensionCounts?.target_audience || {};
  const dateFilterOptions = [
    { value: 'today', label: t('filter.today') },
    { value: 'this_week', label: t('filter.this_week') },
    { value: 'this_month', label: t('filter.this_month') },
  ];
  const ageGroupOptions = [
    { value: 'all', label: t('age.all') },
    { value: 'children', label: t('age.children') },
    { value: 'teens', label: t('age.youth') },
    { value: 'adults', label: t('age.adults') },
    { value: 'seniors', label: t('age.seniors') },
  ];
  return (
  <div className="grid grid-cols-2 gap-3">
    <Select value={pendingFilters.wijk || 'all'} onValueChange={(v) => onPendingFilterChange('wijk', v)}>
      <SelectTrigger className="rounded-xl"><MapPin className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.all_wijken')} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('filter.all_wijken')}</SelectItem>
        {wijken.map(wijk => <SelectItem key={wijk} value={wijk}>{wijk}<CountBadge n={wc[wijk]} /></SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={pendingFilters.activity_type || 'all'} onValueChange={(v) => onPendingFilterChange('activity_type', v)}>
      <SelectTrigger className="rounded-xl"><Tag className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('activity.type')} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('filter.all_types')}</SelectItem>
        {activityTypes.map(type => <SelectItem key={type} value={type}><span className="mr-1">{activityTypeLabels[type]?.icon || ''}</span>{activityTypeLabels[type]?.label || type}<CountBadge n={ac[type]} /></SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={pendingFilters.date_filter || 'all'} onValueChange={(v) => onPendingFilterChange('date_filter', v)}>
      <SelectTrigger className="rounded-xl"><Calendar className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.when')} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('filter.always')}</SelectItem>
        {dateFilterOptions.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
      </SelectContent>
    </Select>
    <Select value={pendingFilters.day || 'all'} onValueChange={(v) => onPendingFilterChange('day', v)}>
      <SelectTrigger className="rounded-xl" data-testid="filter-day-trigger"><CalendarDays className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.day')} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('filter.any_day')}</SelectItem>
        {WEEKDAY_FILTER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{t(o.key)}</SelectItem>)}
      </SelectContent>
    </Select>
    {pendingSourceFilter !== undefined && onPendingSourceFilterChange && (
    <Select value={pendingFilters.target_audience || 'all'} onValueChange={(v) => onPendingFilterChange('target_audience', v)}>
      <SelectTrigger className="rounded-xl"><Users className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.for_who')} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('filter.for_who')}</SelectItem>
        <SelectItem value="Kinderen">{t('age.children')}<CountBadge n={tc['Kinderen']} /></SelectItem>
        <SelectItem value="Jongeren">{t('age.youth')}<CountBadge n={tc['Jongeren']} /></SelectItem>
        <SelectItem value="Volwassenen">{t('age.adults')}<CountBadge n={tc['Volwassenen']} /></SelectItem>
        <SelectItem value="Senioren">{t('age.seniors')}<CountBadge n={tc['Senioren']} /></SelectItem>
        <SelectItem value="Iedereen">{t('age.everyone')}<CountBadge n={tc['Iedereen']} /></SelectItem>
      </SelectContent>
    </Select>
    )}
    {/* Cost filter */}
    <Select value={pendingFilters.cost_filter || 'all'} onValueChange={(v) => onPendingFilterChange('cost_filter', v)}>
      <SelectTrigger className="rounded-xl" data-testid="filter-cost-trigger"><Euro className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.costs_label')} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t('filter.all_costs')}</SelectItem>
        <SelectItem value="free">{t('filter.cost_free')}</SelectItem>
        <SelectItem value="under5">{t('filter.cost_under5')}</SelectItem>
        <SelectItem value="5to10">{t('filter.cost_5to10')}</SelectItem>
        <SelectItem value="over10">{t('filter.cost_over10')}</SelectItem>
        <SelectItem value="subscription">{t('filter.cost_subscription')}</SelectItem>
      </SelectContent>
    </Select>
  </div>
  );
};

// Telt het aantal actieve pending-filters voor de badge.
const countActive = (pendingFilters, pendingSourceFilter) => [
  pendingFilters.wijk,
  pendingFilters.activity_type,
  pendingFilters.date_filter,
  pendingFilters.day,
  pendingFilters.cost_filter,
  pendingFilters.age_group && pendingFilters.age_group !== 'all' ? pendingFilters.age_group : null,
  pendingFilters.target_audience && pendingFilters.target_audience !== 'all' ? pendingFilters.target_audience : null,
  pendingSourceFilter && pendingSourceFilter !== 'all' ? pendingSourceFilter : null,
].filter(Boolean).length;

export const MobileFilters = ({ pendingFilters, wijken, activityTypes, filtersOpen, setFiltersOpen, hasActiveFilters, onPendingFilterChange, onApply, clearFilters, t, pendingSourceFilter, onPendingSourceFilterChange }) => {
  const dimensionCounts = useDimensionCounts(pendingFilters);
  const wc = dimensionCounts?.wijk || {};
  const ac = dimensionCounts?.activity_type || {};
  const tc = dimensionCounts?.target_audience || {};
  const activeCount = countActive(pendingFilters, pendingSourceFilter);
  const { count: liveCount, loading: liveLoading } = useFilterCount(pendingFilters, pendingSourceFilter, filtersOpen);

  return (
    <section className="lg:hidden animate-fade-in-up stagger-3">
      {/* Inline filter trigger (bovenaan zichtbaar) */}
      <Card className="p-3 rounded-2xl bg-white border border-primary/5 shadow-soft">
        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={() => setFiltersOpen(true)}
            className="flex items-center gap-2 font-heading font-bold text-primary"
            data-testid="open-filters-btn"
          >
            <SlidersHorizontal className="w-5 h-5 text-brand" aria-hidden="true" />
            {t('filter.filters')}
            {hasActiveFilters && (
              <Badge className="bg-brand text-white ml-2" data-testid="filter-active-count">
                {activeCount}
              </Badge>
            )}
            <ChevronDown className="w-4 h-4 text-muted" aria-hidden="true" />
          </button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground hover:text-primary">
              <X className="w-4 h-4 mr-1" aria-hidden="true" />
              {t('filter.clear')}
            </Button>
          )}
        </div>
      </Card>

      {/* Bottom Sheet — opent als 'filtersOpen' true is.
          z-[10000] om boven de mobiele bottom-nav (z-9999) uit te komen,
          anders is de 'Toon resultaten' knop onderaan niet zichtbaar. */}
      <Sheet open={filtersOpen} onOpenChange={setFiltersOpen}>
        <SheetContent side="bottom" className="rounded-t-3xl max-h-[85vh] flex flex-col p-0 z-[10000]" data-testid="mobile-filter-sheet">
          <SheetHeader className="px-5 pt-5 pb-2 border-b border-gray-100">
            <SheetTitle className="font-heading font-bold text-xl text-primary flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-brand" aria-hidden="true" />
              {t('filter.filters')}
              {activeCount > 0 && (
                <Badge className="bg-brand text-white">{activeCount}</Badge>
              )}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
            <FilterSelects pendingFilters={pendingFilters} wijken={wijken} activityTypes={activityTypes} onPendingFilterChange={onPendingFilterChange} t={t} pendingSourceFilter={pendingSourceFilter} onPendingSourceFilterChange={onPendingSourceFilterChange} />
          </div>

          {/* Sticky bottom CTA — toont live resultaat-teller.
              pb-[env(safe-area-inset-bottom)] respecteert iOS home-indicator. */}
          <div className="px-5 py-4 border-t border-gray-100 bg-white sticky bottom-0 space-y-2 pb-[max(1rem,env(safe-area-inset-bottom))]">
            {hasActiveFilters && (
              <Button
                variant="ghost"
                onClick={clearFilters}
                className="w-full text-muted-foreground hover:text-primary"
                data-testid="sheet-clear-btn"
              >
                <X className="w-4 h-4 mr-1" aria-hidden="true" />
                {t('filter.clear')}
              </Button>
            )}
            <Button
              onClick={() => { onApply(); setFiltersOpen(false); }}
              className="w-full bg-hoi-coral hover:bg-hoi-coral/90 text-black font-heading font-semibold py-6 rounded-full"
              data-testid="mobile-apply-filters-btn"
            >
              {liveLoading ? (
                t('common.apply_filters')
              ) : liveCount !== null ? (
                liveCount === 1 ? t('filter.show_one_result') : t('filter.show_n_results', { count: liveCount })
              ) : (
                t('common.apply_filters')
              )}
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </section>
  );
};

export const SidebarFilters = ({ pendingFilters, wijken, activityTypes, hasActiveFilters, onPendingFilterChange, onApply, clearFilters, t, pendingSourceFilter, onPendingSourceFilterChange }) => {
  const dimensionCounts = useDimensionCounts(pendingFilters);
  const wc = dimensionCounts?.wijk || {};
  const ac = dimensionCounts?.activity_type || {};
  const tc = dimensionCounts?.target_audience || {};
  const dateOpts = [
    { value: 'today', label: t('filter.today') },
    { value: 'this_week', label: t('filter.this_week') },
    { value: 'this_month', label: t('filter.this_month') },
  ];
  const ageOpts = [
    { value: 'all', label: t('age.all') },
    { value: 'children', label: t('age.children') },
    { value: 'teens', label: t('age.youth') },
    { value: 'adults', label: t('age.adults') },
    { value: 'seniors', label: t('age.seniors') },
  ];
  return (
  <Card className="p-5 rounded-2xl bg-white border border-primary/5 shadow-soft sticky top-24">
    <h3 className="font-heading font-bold text-base text-primary mb-4 flex items-center gap-2">
      <SlidersHorizontal className="w-4 h-4 text-brand" aria-hidden="true" />
      {t('filter.filters')}
      {hasActiveFilters && (
        <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-xs text-muted-foreground hover:text-primary">
          {t('filter.clear')}
        </Button>
      )}
    </h3>
    <div className="space-y-3">
      <Select value={pendingFilters.wijk || 'all'} onValueChange={(v) => onPendingFilterChange('wijk', v)}>
        <SelectTrigger className="rounded-xl w-full"><MapPin className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.all_wijken')} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filter.all_wijken')}</SelectItem>
          {wijken.map(wijk => <SelectItem key={wijk} value={wijk}>{wijk}<CountBadge n={wc[wijk]} /></SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={pendingFilters.activity_type || 'all'} onValueChange={(v) => onPendingFilterChange('activity_type', v)}>
        <SelectTrigger className="rounded-xl w-full"><Tag className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.all_types')} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filter.all_types')}</SelectItem>
          {activityTypes.map(type => <SelectItem key={type} value={type}><span className="mr-1">{activityTypeLabels[type]?.icon || ''}</span>{activityTypeLabels[type]?.label || type}<CountBadge n={ac[type]} /></SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={pendingFilters.date_filter || 'all'} onValueChange={(v) => onPendingFilterChange('date_filter', v)}>
        <SelectTrigger className="rounded-xl w-full"><Calendar className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.when')} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filter.always')}</SelectItem>
          {dateOpts.map(opt => <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>)}
        </SelectContent>
      </Select>
      <Select value={pendingFilters.day || 'all'} onValueChange={(v) => onPendingFilterChange('day', v)}>
        <SelectTrigger className="rounded-xl w-full" data-testid="sidebar-filter-day-trigger"><CalendarDays className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.day')} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filter.any_day')}</SelectItem>
          {WEEKDAY_FILTER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{t(o.key)}</SelectItem>)}
        </SelectContent>
      </Select>
      {pendingSourceFilter !== undefined && onPendingSourceFilterChange && (
      <Select value={pendingFilters.target_audience || 'all'} onValueChange={(v) => onPendingFilterChange('target_audience', v)}>
        <SelectTrigger className="rounded-xl w-full"><Users className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.for_who')} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filter.for_who')}</SelectItem>
          <SelectItem value="Kinderen">{t('age.children')}<CountBadge n={tc['Kinderen']} /></SelectItem>
          <SelectItem value="Jongeren">{t('age.youth')}<CountBadge n={tc['Jongeren']} /></SelectItem>
          <SelectItem value="Volwassenen">{t('age.adults')}<CountBadge n={tc['Volwassenen']} /></SelectItem>
          <SelectItem value="Senioren">{t('age.seniors')}<CountBadge n={tc['Senioren']} /></SelectItem>
          <SelectItem value="Iedereen">{t('age.everyone')}<CountBadge n={tc['Iedereen']} /></SelectItem>
        </SelectContent>
      </Select>
      )}
      <Select value={pendingFilters.cost_filter || 'all'} onValueChange={(v) => onPendingFilterChange('cost_filter', v)}>
        <SelectTrigger className="rounded-xl w-full" data-testid="sidebar-filter-cost-trigger"><Euro className="w-4 h-4 mr-2 text-muted" /><SelectValue placeholder={t('filter.costs_label')} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">{t('filter.all_costs')}</SelectItem>
          <SelectItem value="free">{t('filter.cost_free')}</SelectItem>
          <SelectItem value="under5">{t('filter.cost_under5')}</SelectItem>
          <SelectItem value="5to10">{t('filter.cost_5to10')}</SelectItem>
          <SelectItem value="over10">{t('filter.cost_over10')}</SelectItem>
          <SelectItem value="subscription">{t('filter.cost_subscription')}</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <Button
      className="w-full mt-4 bg-hoi-coral hover:bg-hoi-coral/90 text-black font-heading font-semibold"
      data-testid="sidebar-apply-filters-btn"
      onClick={onApply}
    >
      {t('common.apply_filters')}
    </Button>

    {hasActiveFilters && (
      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t">
        {pendingFilters.wijk && (
          <Badge variant="secondary" className="rounded-full px-3 py-1 cursor-pointer hover:bg-secondary" onClick={() => { onPendingFilterChange('wijk', ''); }}>
            <MapPin className="w-3 h-3 mr-1" />{pendingFilters.wijk}<X className="w-3 h-3 ml-1" />
          </Badge>
        )}
        {pendingFilters.activity_type && (
          <Badge variant="secondary" className="rounded-full px-3 py-1 cursor-pointer hover:bg-secondary" onClick={() => { onPendingFilterChange('activity_type', ''); }}>
            <span className="mr-1">{activityTypeLabels[pendingFilters.activity_type]?.icon}</span>{activityTypeLabels[pendingFilters.activity_type]?.label}<X className="w-3 h-3 ml-1" />
          </Badge>
        )}
      </div>
    )}
  </Card>
  );
};

export default FilterSelects;
;
